import React, { useState, useMemo, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  Divider,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';

/* ── Section label ── */
const SectionHeader = ({ label, icon }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1,
    px: 2, py: 1.2,
    background: 'linear-gradient(135deg, #2d3748 0%, #3d4a5c 100%)',
    color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: 0.3,
    textTransform: 'uppercase', borderRadius: 1, mx: 2, mt: 1.5,
    position: 'relative',
    '&::before': {
      content: '""', position: 'absolute',
      left: 0, top: 0, bottom: 0, width: 4,
      background: '#ff9900', borderRadius: 1,
    },
  }}>
    {icon}
    {label}
  </Box>
);

/* ── Main component ── */
const CardVisibilityDrawer = ({
  open,
  onClose,
  dataCardsOrder,
  chartCardsOrder,
  hiddenCards,
  onHiddenCardsChange,
  groupDataCardsByCategory,
  groupChartCardsByCategory,
  sectionTitleMap,
}) => {
  const [search, setSearch] = useState('');

  /* Group cards */
  const dataGroups = useMemo(() => {
    if (!dataCardsOrder?.length) return {};
    return groupDataCardsByCategory(dataCardsOrder);
  }, [dataCardsOrder, groupDataCardsByCategory]);

  const chartGroups = useMemo(() => {
    if (!chartCardsOrder?.length) return {};
    return groupChartCardsByCategory(chartCardsOrder);
  }, [chartCardsOrder, groupChartCardsByCategory]);

  /* Filtered cards by search */
  const matchesSearch = useCallback((card) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (card.title || card.id || '').toLowerCase().includes(q);
  }, [search]);

  /* Toggle helpers */
  const isHidden = useCallback((cardId) => !!hiddenCards[cardId], [hiddenCards]);

  const toggleCard = useCallback((cardId) => {
    const next = { ...hiddenCards, [cardId]: !hiddenCards[cardId] };
    if (!next[cardId]) delete next[cardId];
    onHiddenCardsChange(next);
  }, [hiddenCards, onHiddenCardsChange]);

  const toggleGroup = useCallback((cards) => {
    const visible = cards.filter((c) => !hiddenCards[c.id]);
    const shouldHideAll = visible.length > 0;
    const next = { ...hiddenCards };
    cards.forEach((c) => {
      if (shouldHideAll) next[c.id] = true;
      else delete next[c.id];
    });
    onHiddenCardsChange(next);
  }, [hiddenCards, onHiddenCardsChange]);

  const showAll = useCallback(() => onHiddenCardsChange({}), [onHiddenCardsChange]);

  const hideAll = useCallback(() => {
    const next = {};
    [...(dataCardsOrder || []), ...(chartCardsOrder || [])].forEach((c) => { next[c.id] = true; });
    onHiddenCardsChange(next);
  }, [dataCardsOrder, chartCardsOrder, onHiddenCardsChange]);

  /* Stats */
  const totalCards = (dataCardsOrder?.length || 0) + (chartCardsOrder?.length || 0);
  const hiddenCount = Object.keys(hiddenCards).length;

  /* ── Render a group of cards ── */
  const renderGroup = (groupLabel, cards) => {
    const filtered = (cards || []).filter(matchesSearch);
    if (filtered.length === 0) return null;

    const visibleInGroup = filtered.filter((c) => !isHidden(c.id));
    const allVisible = visibleInGroup.length === filtered.length;
    const someVisible = visibleInGroup.length > 0 && !allVisible;

    return (
      <Box key={groupLabel} sx={{ mb: 0.5 }}>
        <Box sx={{ px: 2, pt: 1 }}>
          <FormControlLabel
            sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: 13, color: '#475569' } }}
            control={
              <Checkbox
                size="small"
                checked={allVisible}
                indeterminate={someVisible}
                onChange={() => toggleGroup(filtered)}
                sx={{ '&.Mui-checked': { color: '#ff9900' }, '&.MuiCheckbox-indeterminate': { color: '#ff9900' } }}
              />
            }
            label={groupLabel}
          />
        </Box>
        {filtered.map((card) => (
          <Box key={card.id} sx={{ pl: 5, pr: 2 }}>
            <FormControlLabel
              sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
              control={
                <Checkbox
                  size="small"
                  checked={!isHidden(card.id)}
                  onChange={() => toggleCard(card.id)}
                  sx={{ '&.Mui-checked': { color: '#ff9900' } }}
                />
              }
              label={card.title || card.id}
            />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 380, maxWidth: '90vw', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #2d3748 0%, #3d4a5c 100%)',
        color: '#fff',
      }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VisibilityIcon fontSize="small" />
          Visibilidad de indicadores
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Search + bulk actions ── */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar indicador..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" variant="outlined" onClick={showAll}
            sx={{ fontSize: 11, textTransform: 'none', borderColor: '#e2e8f0', color: '#475569' }}>
            Mostrar todos
          </Button>
          <Button size="small" variant="outlined" onClick={hideAll}
            sx={{ fontSize: 11, textTransform: 'none', borderColor: '#e2e8f0', color: '#475569' }}>
            Ocultar todos
          </Button>
          <Typography variant="caption" sx={{ ml: 'auto', color: '#94a3b8' }}>
            {totalCards - hiddenCount}/{totalCards} visibles
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* ── Card list ── */}
      <Box sx={{ flex: 1, overflow: 'auto', pb: 2 }}>
        {/* Data cards (KPIs) */}
        <SectionHeader label="Indicadores (KPI)" icon={<DashboardIcon sx={{ fontSize: 16 }} />} />
        {Object.entries(dataGroups).map(([cat, cards]) => {
          if (!cards?.length) return null;
          const label = sectionTitleMap?.[cat] || cat;
          return renderGroup(label, cards);
        })}

        {/* Chart cards */}
        <SectionHeader label="Gráficas" icon={<BarChartIcon sx={{ fontSize: 16 }} />} />
        {Object.entries(chartGroups).map(([cat, cards]) => {
          if (!cards?.length) return null;
          return renderGroup(cat, cards);
        })}
      </Box>
    </Drawer>
  );
};

export default React.memo(CardVisibilityDrawer);
